/**
 * This file defines helper functions needed for the Select
 * and Combobox components.
 */

import {
  SingleOption,
  type OptionGroup,
  type Options,
} from "../types.ts/types";

/**
 * Check if the first element has both 'group' and 'items' properties
 */
export const isGroupedOptions = <T extends SingleOption = SingleOption>(
  options: Options<T>,
): options is OptionGroup<T>[] => {
  return (
    options.length > 0 &&
    typeof options[0] === "object" &&
    options[0] !== null &&
    "group" in options[0] && // Ensure 'group' exists
    "items" in options[0] // Ensure 'items' exists
  );
};

/**
 * Check if the first element is a string.
 */
export const isStringOptions = <T extends SingleOption = SingleOption>(
  options: Options<T>,
): options is string[] => {
  return options.length > 0 && typeof options[0] === "string";
};

export type OptionsSorting = "asc" | "desc" | "none";
export const sortOptions = <T extends SingleOption = SingleOption>(
  options: Options<T>,
  sorting: OptionsSorting,
): Options<T> => {
  if (sorting === "none") {
    return options;
  }

  if (isGroupedOptions(options)) {
    const newOptions = [...options];

    // Sort groups
    newOptions.sort(
      sorting === "asc"
        ? (a, b) => a.group.localeCompare(b.group)
        : (a, b) => b.group.localeCompare(a.group),
    );

    // Sort items
    newOptions.forEach((group, i, arr) => {
      arr[i].items = group.items.sort(
        sorting === "asc"
          ? (a, b) => a.value.localeCompare(b.value)
          : (a, b) => b.value.localeCompare(a.value),
      );
    });

    return newOptions;
  } else if (isStringOptions(options)) {
    return sorting === "asc"
      ? options.toSorted()
      : options.toSorted().reverse();
  } else {
    return options.toSorted(
      sorting === "asc"
        ? (a, b) => a.value.localeCompare(b.value)
        : (a, b) => b.value.localeCompare(a.value),
    );
  }
};

/**
 * Converts grouped options to a flat array.
 */
export const getFlatOptions = <T extends SingleOption = SingleOption>(
  options: Options<T>,
): T[] => {
  if (isGroupedOptions(options)) {
    return options.flatMap((group) => group.items);
  } else if (isStringOptions(options)) {
    return options.map((v) => ({ label: v, value: v })) as T[];
  }

  return options;
};
